import { CSSProperties, ReactNode, useState } from 'react';
import { Menu, PanelLeftOpen } from 'lucide-react';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import ThemeToggle from './ThemeToggle';
import OnboardingWizard from '../onboarding/OnboardingWizard';
import { useSidebarCollapsed } from '../../hooks/useSidebarCollapsed';

type LayoutProps = {
  children: ReactNode;
  title?: ReactNode;      // título de la página/conversación, a la izquierda de la cabecera
  actions?: ReactNode;    // íconos junto al perfil (p. ej. Fuentes, Asistente); el botón de tema va siempre al final
  search?: ReactNode;     // buscador de la pantalla, a continuación del título (DESIGN.md §11.1)
  rightInset?: number;    // px reservados a la derecha, bajo la cabecera, para paneles acoplados (desde lg)
};

/**
 * Cabecera blanca con borde y perfil a la derecha (diseño de 70e4cef). Alto fijo de 85 px (84 + borde):
 * las páginas de alto completo restan exactamente eso (`100vh-85px`).
 */
export default function Layout({ children, title, actions, search, rightInset = 0 }: LayoutProps) {
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <Sidebar collapsed={collapsed} onToggle={toggleCollapsed} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
      <div className={`transition-[padding] duration-200 motion-reduce:transition-none ${collapsed ? 'lg:pl-0' : 'lg:pl-64'}`}>
        <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
          <div className="px-4 lg:px-8 h-[84px] flex items-center justify-between gap-3">
            <div className={`flex items-center gap-2 min-w-0 ${search ? 'flex-1' : ''}`}>
              <button className="lg:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-50" aria-label="Abrir menú" onClick={() => setMobileOpen(true)}>
                <Menu className="w-6 h-6" />
              </button>
              {collapsed && (
                <button className="hidden lg:inline-flex p-2 rounded-lg text-gray-500 hover:bg-gray-50" aria-label="Mostrar barra lateral" title="Mostrar barra lateral (Ctrl+B)" onClick={toggleCollapsed}>
                  <PanelLeftOpen className="w-5 h-5" />
                </button>
              )}
              {/* Con buscador, el título ocupa 300 px para que el buscador empiece en la misma x en todas las pantallas */}
              {title && <div className={`text-sm font-medium text-gray-900 truncate ${search ? 'w-[300px] shrink-0' : ''}`}>{title}</div>}
              {search && <div className="hidden md:block flex-1 max-w-[560px] min-w-[240px]">{search}</div>}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="flex items-center gap-1">{actions}<ThemeToggle /></div>
              <ProfileMenu />
            </div>
          </div>
        </header>

        <div className="transition-[padding] duration-200 motion-reduce:transition-none lg:pr-[var(--right-inset)]" style={{ '--right-inset': `${rightInset}px` } as CSSProperties}>
          <main className="p-4 lg:p-8">{children}</main>
        </div>
        <OnboardingWizard />
      </div>
    </div>
  );
}
